import {
    AuthSettings,
    ClassForTeamsSettings,
    OrgSettings,
    FeatureSettings,
    OrganizationType,
    RecordingSettings,
    TerminologySettings,
    ZoomAppId
} from '../../../types/adminConfig';
import { SchoolProperties, SchoolSettings } from '../../../types/apiTypes';

const transformSettingsData = (flatData: SchoolSettings & SchoolProperties): OrgSettings => {
    const organizationType: OrganizationType = flatData.organization_type;
    const zoomAppId: ZoomAppId = flatData.zoom_app_id;

    const terminologySettings: TerminologySettings = {
        hostTitle: flatData.host_title,
        assistantsTitle: flatData.assistants_title,
        studentsTitle: flatData.students_title,
        rosterTitle: flatData.roster_title,
        gradebookTitle: flatData.gradebook_title,
        documentTitle: flatData.document_title,
        proctoringTitle: flatData.proctoring_title
    };

    const featureSettings: FeatureSettings = {
        classDocument: flatData.has_document,
        classGradebook: flatData.has_gradebook,
        classAssessments: flatData.has_assessments,
        lms: flatData.has_lms,
        enableLogAutoUpload: flatData.enable_log_auto_upload,
        hasPanicMode: flatData.has_panic_mode,
        surveys: flatData.has_surveys,
        polling: flatData.has_polling,
        proctoring: flatData.has_proctoring,
        whiteboard: flatData.has_whiteboard,
        assignments: flatData.has_assignments,
        chat: flatData.has_chat,
        showMeetingInfo: flatData.show_meeting_info,
        loseFocusTracking: flatData.has_lose_focus_tracking,
        webApp: flatData.has_web_app,
        forceWebAppChromebooks: flatData.force_web_app_chromebooks,
        hasShareFiles: flatData.has_share_files,
        allowApp: flatData.has_app,
        allowZoom: flatData.has_zoom,
        autoTranscriptions: flatData.auto_transcriptions,
        classNotes: flatData.class_notes,
        sendInviteEmails: flatData.send_invite_emails,
        promptTeachersForFeedback: flatData.prompt_teachers_for_feedback,
        promptStudentsForFeedback: flatData.prompt_students_for_feedback,
        notifyOutdatedClassVersion: flatData.notify_outdated_class_version,
        enforceClassMinimumVersion: flatData.enforce_class_minimum_version,
        recordingLegacyZoomList: !flatData.recording_interactive_player,
        recordingInitialPermission: flatData.recording_initial_permission,
        recordingIngestMode: flatData.recording_ingest_mode,
        recordingParticipantsCanView: flatData.recording_participants_can_view,
        recordingRestrictGallery: flatData.recording_restrict_gallery,
        recordingTabAccess: flatData.recording_tab_access,
        attendanceTabAccess: flatData.attendance_tab_access,
        restrictSessionsRecordingsByGroups: flatData.restrict_sessions_recordings_by_groups,
        google: flatData.has_google,
        msOffice: flatData.has_ms_office,
        aiTa: flatData.has_ai_ta,
        classAccess: flatData.class_access,
        allowInstructorSwap: flatData.allow_instructor_swap,
        enableBreakoutRoomCloudRecording: flatData.enable_breakout_room_cloud_recording,
        preventLeaveSessionRedirect: flatData.prevent_leave_session_redirect,
        enableBreakoutRoomSecurity: flatData.enable_breakout_room_security,
        whiteboardScreenshotFeature: flatData.whiteboard_screenshot_feature,
        instructorChatDownload: flatData.instructor_chat_download,
        isBbData: flatData.is_bb_data,
        dataPortalAdmin: flatData.data_portal_admin,
        dataPortalBusinessAnalyst: flatData.data_portal_business_analyst,
        dataPortalClassBusinessAnalyst: flatData.data_portal_class_business_analyst,
        dataPortalCustomerSupport: flatData.data_portal_customer_support,
        dataPortalInstructor: flatData.data_portal_instructor,
        dataPortalStudent: flatData.data_portal_student,
        allowAiForSupport: flatData.allow_ai_for_support,
        allowAiForInsights: flatData.allow_ai_for_insights,
        options: flatData.options
    };

    const recordingSettings: RecordingSettings = {
        autoSave: flatData.recording_auto_save,
        cloudSave: flatData.recording_cloud_save,
        localSave: flatData.recording_local_save,
        participantsCanDownload: flatData.recording_participants_can_download
    };

    const authSettings: AuthSettings = {
        vanityUrl: flatData.vanity_url,
        domains: flatData.domains?.split(',') ?? [],
        joinMeetingButton: flatData.join_class_by_meeting_id,
        allowPMI: flatData.allow_pmi,
        forcedAuthentication: flatData.forced_authentication,
        autoVerify: flatData.auto_verify,
        unauthorizedUserAccess: flatData.unauth_user_access,
        unauthorizedUserText: flatData.unauth_user_text
    };

    const classForTeamsSettings: ClassForTeamsSettings = {
        azureTenantIds:
            // eslint-disable-next-line no-nested-ternary
            flatData.options.azureTenantIds && flatData.options.azureTenantIds.length > 0
                ? flatData.options.azureTenantIds
                : flatData.azure_tenant_id
                  ? [flatData.azure_tenant_id]
                  : []
    };

    console.log(featureSettings);
    return {
        organizationType,
        terminologySettings,
        featureSettings,
        recordingSettings,
        authSettings,
        classForTeamsSettings,
        zoomAppId
    } satisfies OrgSettings;
};

const flattenData = (enrichedData: OrgSettings): SchoolSettings => {
    const flattenedData: SchoolSettings = {
        organization_type: enrichedData.organizationType,
        host_title: enrichedData.terminologySettings.hostTitle,
        assistants_title: enrichedData.terminologySettings.assistantsTitle,
        students_title: enrichedData.terminologySettings.studentsTitle,
        roster_title: enrichedData.terminologySettings.rosterTitle,
        gradebook_title: enrichedData.terminologySettings.gradebookTitle,
        document_title: enrichedData.terminologySettings.documentTitle,
        proctoring_title: enrichedData.terminologySettings.proctoringTitle,
        vanity_url: enrichedData.authSettings.vanityUrl,
        domains: enrichedData.authSettings.domains.join(','),
        allow_pmi: enrichedData.authSettings.allowPMI,
        forced_authentication: enrichedData.authSettings.forcedAuthentication,
        enable_log_auto_upload: enrichedData.featureSettings.enableLogAutoUpload,
        has_document: enrichedData.featureSettings.classDocument,
        has_gradebook: enrichedData.featureSettings.classGradebook,
        has_assessments: enrichedData.featureSettings.classAssessments,
        has_lms: enrichedData.featureSettings.lms,
        has_panic_mode: enrichedData.featureSettings.hasPanicMode,
        has_surveys: enrichedData.featureSettings.surveys,
        has_polling: enrichedData.featureSettings.polling,
        has_proctoring: enrichedData.featureSettings.proctoring,
        has_share_files: enrichedData.featureSettings.hasShareFiles,
        unauth_user_access: enrichedData.authSettings.unauthorizedUserAccess,
        unauth_user_text: enrichedData.authSettings.unauthorizedUserText,
        has_whiteboard: enrichedData.featureSettings.whiteboard,
        has_assignments: enrichedData.featureSettings.assignments,
        has_chat: enrichedData.featureSettings.chat,
        has_lose_focus_tracking: enrichedData.featureSettings.loseFocusTracking,
        has_web_app: enrichedData.featureSettings.webApp,
        force_web_app_chromebooks: enrichedData.featureSettings.forceWebAppChromebooks,
        has_app: enrichedData.featureSettings.allowApp,
        has_zoom: enrichedData.featureSettings.allowZoom,
        zoom_app_id: enrichedData.zoomAppId,
        join_class_by_meeting_id: enrichedData.authSettings.joinMeetingButton,
        auto_verify: enrichedData.authSettings.autoVerify,
        auto_transcriptions: enrichedData.featureSettings.autoTranscriptions,
        class_notes: enrichedData.featureSettings.classNotes,
        send_invite_emails: enrichedData.featureSettings.sendInviteEmails,
        show_meeting_info: enrichedData.featureSettings.showMeetingInfo,
        recording_auto_save: enrichedData.recordingSettings.autoSave,
        recording_cloud_save: enrichedData.recordingSettings.cloudSave,
        recording_local_save: enrichedData.recordingSettings.localSave,
        recording_interactive_player: !enrichedData.featureSettings.recordingLegacyZoomList,
        recording_initial_permission: enrichedData.featureSettings.recordingInitialPermission,
        recording_ingest_mode: enrichedData.featureSettings.recordingIngestMode,
        recording_participants_can_view: enrichedData.featureSettings.recordingParticipantsCanView,
        recording_participants_can_download: enrichedData.recordingSettings.participantsCanDownload,
        recording_restrict_gallery: enrichedData.featureSettings.recordingRestrictGallery,
        recording_tab_access: enrichedData.featureSettings.recordingTabAccess,
        attendance_tab_access: enrichedData.featureSettings.attendanceTabAccess,
        restrict_sessions_recordings_by_groups: enrichedData.featureSettings.restrictSessionsRecordingsByGroups,
        prompt_teachers_for_feedback: enrichedData.featureSettings.promptTeachersForFeedback,
        prompt_students_for_feedback: enrichedData.featureSettings.promptStudentsForFeedback,
        notify_outdated_class_version: enrichedData.featureSettings.notifyOutdatedClassVersion,
        enforce_class_minimum_version: enrichedData.featureSettings.enforceClassMinimumVersion,
        has_google: enrichedData.featureSettings.google,
        has_ms_office: enrichedData.featureSettings.msOffice,
        has_ai_ta: enrichedData.featureSettings.aiTa,
        class_access: enrichedData.featureSettings.classAccess,
        allow_instructor_swap: enrichedData.featureSettings.allowInstructorSwap,
        enable_breakout_room_cloud_recording: enrichedData.featureSettings.enableBreakoutRoomCloudRecording,
        prevent_leave_session_redirect: enrichedData.featureSettings.preventLeaveSessionRedirect,
        enable_breakout_room_security: enrichedData.featureSettings.enableBreakoutRoomSecurity,
        whiteboard_screenshot_feature: enrichedData.featureSettings.whiteboardScreenshotFeature,
        instructor_chat_download: enrichedData.featureSettings.instructorChatDownload,
        is_bb_data: enrichedData.featureSettings.isBbData,
        data_portal_admin: enrichedData.featureSettings.dataPortalAdmin,
        data_portal_business_analyst: enrichedData.featureSettings.dataPortalBusinessAnalyst,
        data_portal_class_business_analyst: enrichedData.featureSettings.dataPortalClassBusinessAnalyst,
        data_portal_customer_support: enrichedData.featureSettings.dataPortalCustomerSupport,
        data_portal_instructor: enrichedData.featureSettings.dataPortalInstructor,
        data_portal_student: enrichedData.featureSettings.dataPortalStudent,
        allow_ai_for_support: enrichedData.featureSettings.allowAiForSupport,
        allow_ai_for_insights: enrichedData.featureSettings.allowAiForInsights,
        azure_tenant_id: null,
        options: {
            ...enrichedData.featureSettings.options,
            azureTenantIds: enrichedData.classForTeamsSettings.azureTenantIds
        }
    };

    return flattenedData;
};

export { transformSettingsData, flattenData };
